import { StoryChiefResponse, StoryChiefPost, BlogFilters } from '../types/blog';

// Use relative API path that works in both development and production
const PROXY_BASE_URL = '/api/storychief';

class StoryChiefAPI {
  private baseURL: string;

  constructor() {
    this.baseURL = PROXY_BASE_URL;
  }

  private async makeRequest<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    // Use relative URL - this will work in both dev and production
    const url = new URL(`${this.baseURL}${endpoint}`, window.location.origin);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) url.searchParams.append(key, value);
      });
    }

    console.log('Making request to:', url.toString());

    const response = await fetch(url.toString(), {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('API Error:', errorData);
      throw new Error(errorData.error || `API error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async getPosts(filters: BlogFilters = {}): Promise<StoryChiefResponse> {
    const params: Record<string, string> = {};
    
    if (filters.page) params.page = filters.page.toString();
    if (filters.per_page) params.per_page = filters.per_page.toString();
    
    if (filters.status) {
      params.status = filters.status;
    } else {
      params.status = 'published'; // Default to published only
    }

    try {
      console.log('Trying /stories endpoint with params:', params);
      return await this.makeRequest<StoryChiefResponse>('/stories', params);
    } catch (error) {
      console.log('Stories endpoint failed, trying /posts...');
      try {
        return await this.makeRequest<StoryChiefResponse>('/posts', params);
      } catch (error2) {
        console.log('Posts endpoint failed, trying /publications/posts...');
        try {
          return await this.makeRequest<StoryChiefResponse>('/publications/posts', params);
        } catch (error3) {
          console.log('Trying /publications/stories...');
          return await this.makeRequest<StoryChiefResponse>('/publications/stories', params);
        }
      }
    }
  }

  // Updated method to get post by ID with full content
  async getPost(id: string | number): Promise<StoryChiefPost> {
    const params: Record<string, string> = {
      'includes[]': [
        'content',
        'tags', 
        'primaryCategory',
        'categories',
        'author',
        'translations',
        'source',
        'custom_fields',
        'destinations',
        'metrics'
      ].join('&includes[]=')
    };

    // Try different endpoint patterns with the ID
    const endpoints = [
      `/stories/${id}`,
      `/posts/${id}`,
      `/publications/stories/${id}`,
      `/publications/posts/${id}`
    ];

    for (const endpoint of endpoints) {
      try {
        console.log(`Trying endpoint: ${endpoint} with includes`);
        
        // Construct URL with multiple includes[] parameters
        const url = new URL(`${this.baseURL}${endpoint}`, window.location.origin);
        
        // Add each include parameter separately
        const includes = [
          'content',
          'tags', 
          'primaryCategory',
          'categories',
          'author',
          'translations',
          'source',
          'custom_fields',
          'destinations',
          'metrics'
        ];
        
        includes.forEach(include => {
          url.searchParams.append('includes[]', include);
        });

        console.log('Full URL with includes:', url.toString());

        const response = await fetch(url.toString(), {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          console.error('API Error:', errorData);
          throw new Error(errorData.error || `API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Success with endpoint:', endpoint);
        
        // Handle both wrapped and direct responses
        if (data.data) {
          return data.data;
        } else if (data.id) {
          return data;
        } else {
          throw new Error('Unexpected response format');
        }
        
      } catch (error) {
        console.log(`Failed with endpoint ${endpoint}:`, error);
        // Continue to next endpoint
      }
    }

    throw new Error(`Post with ID "${id}" not found. Tried all available endpoints.`);
  }

  // Helper method to find post by slug and get its ID
  async getPostBySlug(slug: string): Promise<StoryChiefPost> {
    try {
      // First, get all posts to find the one with matching slug
      const response = await this.getPosts({ per_page: 100, status: 'published' });
      const post = response.data.find(p => p.slug === slug);
      
      if (!post) {
        throw new Error(`Post with slug "${slug}" not found`);
      }

      // Then get the full post content using the ID
      return await this.getPost(post.id);
    } catch (error) {
      console.error('Error finding post by slug:', error);
      throw error;
    }
  }

  async getDraftPosts(filters: BlogFilters = {}): Promise<StoryChiefResponse> {
    return this.getPosts({ ...filters, status: 'draft' });
  }

  async getPublishedPosts(filters: BlogFilters = {}): Promise<StoryChiefResponse> {
    return this.getPosts({ ...filters, status: 'published' });
  }

  async getScheduledPosts(filters: BlogFilters = {}): Promise<StoryChiefResponse> {
    return this.getPosts({ ...filters, status: 'scheduled' });
  }

  async getAllPosts(filters: BlogFilters = {}): Promise<StoryChiefResponse> {
    return this.getPosts({ ...filters, status: 'all' });
  }

  async testConnection(): Promise<boolean> {
    try {
      const response = await fetch('/api/health');
      return response.ok;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }
}

export const storyChiefAPI = new StoryChiefAPI();